import {
  TIMEZONE_OPTIONS,
  TIMEZONE_PICKER_INITIAL, organizationCodeSchema, type CurrentUser } from '@capsio/contracts';
import { useState, type ReactNode } from 'react';
import { api, messageOf } from '../api-client.js';
import { AtmospherePanel } from '../components/AtmospherePanel.js';
import { BusyButton, Field, PasswordMeter } from '../components/Field.js';
import { generateInitialPassword } from '../initial-password.js';

type Step = 'organization' | 'admin' | 'done';

/*
 * 时区候选与初值一律取自锚点定义处（I-7、ADR-0051）——
 * 本文件此前自带一张表，那是与锚点竞争的第二处定义。
 */

function isLoopback(url: string): boolean {
  return /^https?:\/\/(localhost|127\.0\.0\.1|\[?::1\]?|0\.0\.0\.0)(:\d+)?(\/|$)/i.test(url.trim());
}

/** 系统生成的密码要能一眼读清、一键复制，还能不满意就换一串。 */
function PasswordCopy({
  value,
  onRegenerate,
}: {
  value: string;
  onRegenerate?: () => void;
}): ReactNode {
  const [copied, setCopied] = useState(false);

  return (
    <div className="copyfield">
      <span className="val">{value}</span>
      {onRegenerate !== undefined && (
        <button className="btn quiet" type="button" onClick={onRegenerate}>
          换一串
        </button>
      )}
      <button
        className="btn quiet"
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(value).catch(() => undefined);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? '已复制' : '复制'}
      </button>
    </div>
  );
}

const PANEL: Record<Step, { kicker?: string; statement: string; sub?: string; depth: number }> = {
  organization: {
    kicker: '首次初始化',
    statement: '这套系统还没有主人',
    sub: '一套部署对应一个企业。初始化只会发生一次，之后这个页面就关了。',
    depth: 1,
  },
  admin: {
    kicker: '首次初始化',
    statement: '第一个账号',
    sub: '这是系统里唯一一个不是被邀请进来的人。之后所有成员都由它加进来。',
    depth: 2,
  },
  done: {
    kicker: '初始化完成',
    statement: '可以开门了',
    sub: '初始化入口已永久关闭，再访问就跳登录页。',
    depth: 4,
  },
};

export function SetupPage({ onDone }: { onDone: (user: CurrentUser) => void }): ReactNode {
  const [step, setStep] = useState<Step>('organization');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [code, setCode] = useState('');
  // 服务端不知道自己对外是什么地址，但打开这个页面的这次请求带着一个——拿它做默认值。
  const [baseUrl, setBaseUrl] = useState(window.location.origin);
  const [editingUrl, setEditingUrl] = useState(false);
  const [timezone, setTimezone] = useState(TIMEZONE_PICKER_INITIAL);
  const [emailDomain, setEmailDomain] = useState('');
  const [orgErrors, setOrgErrors] = useState<Record<string, string>>({});

  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [adminErrors, setAdminErrors] = useState<Record<string, string>>({});
  /** 非空表示这次用的是系统生成的密码，而不是用户自己填的。 */
  const [generated, setGenerated] = useState<string | null>(null);
  /** 生成密码那条路要用户点一下才进系统，所以把会话先攒在这儿。 */
  const [pending, setPending] = useState<CurrentUser | null>(null);

  /**
   * 初始化令牌。
   *
   * 服务端启动时把它写到 `DATA_DIR/bootstrap-token`（权限 0600），
   * 只有能登上那台机器的人拿得到。**它是「谁先到谁当管理员」这件事的唯一闸门**
   * ——没有它，任何能打开这个页面的人都能把自己设成管理员（MF-04）。
   */
  const [bootstrapToken, setBootstrapToken] = useState('');

  const panel = PANEL[step];

  function submitOrganization(event: React.FormEvent): void {
    event.preventDefault();
    const errors: Record<string, string> = {};
    if (name.trim() === '') errors.name = '请填写企业名称';
    if (!organizationCodeSchema.safeParse(code).success) {
      errors.code = '2–12 位大写英文或数字，首位必须是字母';
    }
    if (!/^https?:\/\/.+/.test(baseUrl.trim())) {
      errors.baseUrl = '请填写完整地址，含 http:// 或 https://';
    }
    if (emailDomain.trim() !== '' && !/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(emailDomain.trim())) {
      errors.emailDomain = '域名格式不对';
    }
    setOrgErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setStep('admin');
  }

  function enter(): void {
    if (pending !== null) onDone(pending);
  }

  async function submitAdmin(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const chosen = generated ?? password;

    const errors: Record<string, string> = {};
    if (adminName.trim() === '') errors.name = '请填写姓名';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail.trim()))
      errors.email = '请填写可用的邮箱地址';
    if (generated === null) {
      if (password.length < 12) errors.password = '密码至少 12 位';
      else if (confirm !== password) errors.confirm = '两次输入不一致';
    }
    setAdminErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setBusy(true);
    setFormError(null);
    try {
      const user = await api.setup({
        organization: {
          name: name.trim(),
          shortName: shortName.trim() === '' ? undefined : shortName.trim(),
          code,
          baseUrl: baseUrl.trim(),
          timezone,
          emailDomain: emailDomain.trim() === '' ? undefined : emailDomain.trim().toLowerCase(),
        },
        admin: { name: adminName.trim(), email: adminEmail.trim(), password: chosen },
      }, bootstrapToken.trim());
      setPending(user);
      setStep('done');
      // 密码是自己填的就直接进系统；系统生成的那条路要等他点「我存好了」。
      if (generated === null) window.setTimeout(() => onDone(user), 0);
    } catch (error) {
      setFormError(messageOf(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="split">
      <AtmospherePanel
        depth={panel.depth}
        kicker={panel.kicker}
        statement={panel.statement}
        sub={panel.sub}
        steps={step === 'done' ? [3, 2] : [{ organization: 1, admin: 2 }[step], 2]}
      />

      <main className="form-side">
        {step === 'organization' && (
          <div className="col roomy">
            <div className="head">
              <span className="step-label">第 1 步 · 共 2 步</span>
              <h1>企业信息</h1>
              <p>填一次，之后在设置里改。</p>
            </div>

            <form onSubmit={submitOrganization}>
              <Field id="org-name" label="企业名称" error={orgErrors.name}>
                <input
                  id="org-name"
                  autoComplete="organization"
                  placeholder="示例科技有限公司"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </Field>

              <div className="pair">
                <Field id="org-short" label="简称" aux="选填">
                  <input
                    id="org-short"
                    placeholder="示例科技"
                    value={shortName}
                    onChange={(event) => setShortName(event.target.value)}
                  />
                </Field>
                <Field id="org-code" label="企业标识" aux="创建后不可改" error={orgErrors.code}>
                  <input
                    id="org-code"
                    className="code-id"
                    spellCheck={false}
                    autoComplete="off"
                    maxLength={12}
                    placeholder="EXAMPLE"
                    value={code}
                    onChange={(event) =>
                      setCode(
                        event.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9]/g, '')
                          .slice(0, 12),
                      )
                    }
                  />
                </Field>
              </div>

              <Field
                id="org-baseurl"
                label="系统访问地址"
                aux="邀请与重置链接用它"
                error={orgErrors.baseUrl}
                hint={
                  <>
                    <p className="sub-hint">
                      从你打开这个页面的地址读到的。员工如果走别的地址进来——内网域名、反向代理、VPN——改成他们用的那个。
                    </p>
                    {isLoopback(baseUrl) && (
                      <p className="msg note">
                        这是本机地址，只有这台机器打得开。邮件里的链接对其他人是死的，而且不会报错。
                      </p>
                    )}
                  </>
                }
              >
                {editingUrl ? (
                  <input
                    id="org-baseurl"
                    spellCheck={false}
                    placeholder="http://hub.内网域名"
                    value={baseUrl}
                    onChange={(event) => setBaseUrl(event.target.value)}
                  />
                ) : (
                  <div className="detected">
                    <span className="det-tag">自动识别</span>
                    <span className="det-val">{baseUrl}</span>
                    <button className="linkbtn" type="button" onClick={() => setEditingUrl(true)}>
                      改一下
                    </button>
                  </div>
                )}
              </Field>

              <div className="pair">
                <Field id="org-tz" label="默认时区">
                  <select
                    id="org-tz"
                    value={timezone}
                    onChange={(event) => setTimezone(event.target.value)}
                  >
                    {TIMEZONE_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  id="org-domain"
                  label="邮箱域名限制"
                  aux="选填"
                  error={orgErrors.emailDomain}
                >
                  <input
                    id="org-domain"
                    spellCheck={false}
                    placeholder="example.com"
                    value={emailDomain}
                    onChange={(event) => setEmailDomain(event.target.value)}
                  />
                </Field>
              </div>

              <button className="btn" type="submit">
                下一步
              </button>
            </form>
          </div>
        )}

        {step === 'admin' && (
          <div className="col roomy">
            <div className="head">
              <span className="step-label">第 2 步 · 共 2 步</span>
              <h1>创建初始管理员</h1>
              <p>用这个邮箱登录系统。</p>
            </div>

            <form onSubmit={(event) => void submitAdmin(event)}>
              <div className="pair">
                <Field id="admin-name" label="姓名" error={adminErrors.name}>
                  <input
                    id="admin-name"
                    autoComplete="name"
                    placeholder="张三"
                    value={adminName}
                    onChange={(event) => setAdminName(event.target.value)}
                  />
                </Field>
                <Field id="admin-email" label="邮箱" error={adminErrors.email}>
                  <input
                    id="admin-email"
                    type="email"
                    autoComplete="email"
                    placeholder="admin@example.com"
                    value={adminEmail}
                    onChange={(event) => setAdminEmail(event.target.value)}
                  />
                </Field>
              </div>

              {generated === null ? (
                <>
                  <Field
                    id="admin-pw"
                    label="密码"
                    aux={
                      <button
                        className="linkbtn"
                        type="button"
                        onClick={() => setGenerated(generateInitialPassword())}
                      >
                        让系统生成
                      </button>
                    }
                    error={adminErrors.password}
                    hint={
                      <>
                        <PasswordMeter value={password} />
                        <p className="sub-hint">至少 12 位。</p>
                      </>
                    }
                  >
                    <input
                      id="admin-pw"
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••••••"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  </Field>

                  <Field id="admin-pw2" label="再输一次" error={adminErrors.confirm}>
                    <input
                      id="admin-pw2"
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••••••"
                      value={confirm}
                      onChange={(event) => setConfirm(event.target.value)}
                    />
                  </Field>
                </>
              ) : (
                <div className="field">
                  <label>
                    <span>密码（系统生成）</span>
                    <span className="aux">
                      <button
                        className="linkbtn"
                        type="button"
                        onClick={() => {
                          setGenerated(null);
                          setAdminErrors({});
                        }}
                      >
                        我自己设
                      </button>
                    </span>
                  </label>
                  <PasswordCopy
                    value={generated}
                    onRegenerate={() => setGenerated(generateInitialPassword())}
                  />
                  <p className="sub-hint">
                    明文只在这一屏和下一屏出现，之后库里只有哈希，任何地方都取不回来。
                  </p>
                </div>
              )}

              <Field
                id="bootstrap-token"
                label="初始化令牌"
                aux="服务器上取"
                hint={
                  <p className="sub-hint">
                    在装了本系统的那台服务器上，打开文件{' '}
                    <code>DATA_DIR/bootstrap-token</code>，把里面那一串整个复制过来。
                    没有它不能初始化——否则任何能打开这个页面的人都能把自己设成管理员。
                  </p>
                }
              >
                <input
                  id="bootstrap-token"
                  value={bootstrapToken}
                  placeholder="从服务器的 bootstrap-token 文件里复制"
                  onChange={(event) => setBootstrapToken(event.target.value)}
                />
              </Field>

              <div className="call info">
                <span className="ic">i</span>
                <p>
                  不验证这个邮箱——这会儿还没有发信通道，也不需要有。
                  <strong>装系统这天一封邮件都不用发</strong>，发信配置进系统后再说。
                </p>
              </div>

              {formError !== null && (
                <div className="call crit" role="alert">
                  <span className="ic">×</span>
                  <p>{formError}</p>
                </div>
              )}

              <div className="row-actions">
                <button className="btn quiet" type="button" onClick={() => setStep('organization')}>
                  上一步
                </button>
                <BusyButton busy={busy} busyLabel="初始化中" type="submit">
                  完成初始化
                </BusyButton>
              </div>
            </form>
          </div>
        )}

        {step === 'done' && (
          <div className="col">
            <svg className="seal" viewBox="0 0 44 44" aria-hidden="true">
              <circle cx="22" cy="22" r="21" />
              <path d="M13.5 22.5 L19.5 28.5 L31 17" />
            </svg>

            {generated === null ? (
              <div className="head">
                <h1>初始化完成</h1>
                <p>正在进入系统…</p>
              </div>
            ) : (
              // 密码是系统给的，用户脑子里没有它。这一屏必须停住，不能像手填那样直接放行。
              <>
                <div className="head">
                  <h1>初始化完成</h1>
                  <p>把这串密码存好，这是它最后一次显示。下次登录要用它。</p>
                </div>
                <div className="recap">
                  <div>
                    <dt>登录邮箱</dt>
                    <dd>{adminEmail.trim()}</dd>
                  </div>
                </div>
                <PasswordCopy value={generated} />
                <div className="call brass">
                  <span className="ic">!</span>
                  <p>忘了也不是死路：配好发信邮箱之后可以走「忘记密码」；在那之前，只能重装。</p>
                </div>
                <button className="btn" type="button" onClick={() => enter()}>
                  我存好了，进入系统
                </button>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
